import { EmptyState, Table, type TableColumn } from '@today/ui-components'
import { Button, Dialog } from '@today/ui-primitives'
import type { ReactElement } from 'react'
import { ProductConfirmDialogView } from '../product/product-confirm-dialog.view'
import { ErrorText, TextInput } from '../product/product-fields'
import { CheckboxInput } from './org-fields'
import type { OrgConfirmState, OrgEditorState, OrgTypeFormView, OrgTypeRowView, Translate } from './org-types'

/**
 * Props for {@link OrgTypesEditorView}.
 *
 * @property rows - Every type in the API's order (sortOrder, then code).
 * @property parentTypeOptions - Type codes the open dialog may allow as parents (the edited type excluded).
 * @property onNew - Present only with `organization_type.write` (also gates Edit and Delete).
 */
export interface OrgTypesEditorViewProps {
	t: Translate
	state: 'loading' | 'error' | 'ready'
	rows: OrgTypeRowView[]
	editor: OrgEditorState<OrgTypeFormView> | null
	parentTypeOptions: string[]
	deleting: OrgConfirmState | null
	isBusy: boolean
	onRetry: () => void
	onEditorChange: <K extends keyof OrgTypeFormView>(field: K, value: OrgTypeFormView[K]) => void
	onEditorSubmit: () => void
	onEditorClose: () => void
	onConfirmDelete: () => void
	onCancelDelete: () => void
	onNew?: () => void
	onEdit?: (id: string) => void
	onDelete?: (id: string) => void
}

const yesNo = (t: Translate, value: boolean) => t(value ? 'types.yes' : 'types.no')

const columns = (props: OrgTypesEditorViewProps): TableColumn<OrgTypeRowView>[] => {
	const { t } = props
	return [
		{ key: 'code', header: t('types.fields.code') },
		{ key: 'name', header: t('types.fields.name') },
		{ key: 'isPhysical', header: t('types.fields.isPhysical'), cell: (row) => yesNo(t, row.isPhysical) },
		{ key: 'canHaveUsers', header: t('types.fields.canHaveUsers'), cell: (row) => yesNo(t, row.canHaveUsers) },
		{ key: 'sortOrder', header: t('types.fields.sortOrder'), cell: (row) => String(row.sortOrder) },
		{
			key: 'allowedParentTypeCodes',
			header: t('types.fields.allowedParentTypeCodes'),
			cell: (row) => row.allowedParentTypeCodes?.join(', ') ?? t('types.any'),
		},
		{
			key: 'actions',
			header: '',
			cell: (row) =>
				props.onEdit ? (
					<div className="flex gap-2">
						<Button variant="muted" size="sm" onPress={() => props.onEdit?.(row.id)}>
							{t('types.edit')}
						</Button>
						<Button variant="danger" size="sm" onPress={() => props.onDelete?.(row.id)}>
							{t('types.delete')}
						</Button>
					</div>
				) : null,
		},
	]
}

function ParentTypes({ props, editor }: { props: OrgTypesEditorViewProps; editor: OrgEditorState<OrgTypeFormView> }) {
	const { t } = props
	const selected = new Set(editor.values.allowedParentTypeCodes)
	const toggle = (code: string, on: boolean) =>
		props.onEditorChange(
			'allowedParentTypeCodes',
			props.parentTypeOptions.filter((c) => (c === code ? on : selected.has(c))),
		)
	return (
		<fieldset className="flex flex-col gap-1">
			<legend className="text-ds-sm font-medium text-ds-text-muted">{t('types.fields.allowedParentTypeCodes')}</legend>
			<CheckboxInput
				label={t('types.fields.anyParent')}
				checked={editor.values.anyParent}
				onChange={(v) => props.onEditorChange('anyParent', v)}
			/>
			{props.parentTypeOptions.map((code) => (
				<CheckboxInput
					key={code}
					label={code}
					checked={selected.has(code)}
					disabled={editor.values.anyParent}
					onChange={(v) => toggle(code, v)}
				/>
			))}
			<ErrorText message={editor.errors.allowedParentTypeCodes} />
		</fieldset>
	)
}

function EditorDialog({ props }: { props: OrgTypesEditorViewProps }): ReactElement | null {
	const { t, editor } = props
	if (!editor) return null
	const title = t(editor.mode === 'create' ? 'types.dialogNew' : 'types.dialogEdit')
	const text = (name: 'code' | 'name' | 'sortOrder', disabled = false) => (
		<TextInput
			label={t(`types.fields.${name}`)}
			value={editor.values[name]}
			error={editor.errors[name]}
			disabled={disabled}
			onChange={(v) => props.onEditorChange(name, v)}
		/>
	)
	return (
		<Dialog isOpen onOpenChange={(open) => (open ? undefined : props.onEditorClose())} title={title}>
			<form
				aria-label={title}
				className="flex flex-col gap-3"
				onSubmit={(e) => {
					e.preventDefault()
					props.onEditorSubmit()
				}}
			>
				{text('code', editor.mode === 'edit')}
				{text('name')}
				<CheckboxInput
					label={t('types.fields.isPhysical')}
					checked={editor.values.isPhysical}
					onChange={(v) => props.onEditorChange('isPhysical', v)}
				/>
				<CheckboxInput
					label={t('types.fields.canHaveUsers')}
					checked={editor.values.canHaveUsers}
					onChange={(v) => props.onEditorChange('canHaveUsers', v)}
				/>
				{text('sortOrder')}
				<ParentTypes props={props} editor={editor} />
				<ErrorText message={editor.formError} />
				<div className="flex gap-2">
					<Button type="submit" isDisabled={props.isBusy}>
						{t('types.save')}
					</Button>
					<Button variant="muted" onPress={props.onEditorClose}>
						{t('types.cancel')}
					</Button>
				</div>
			</form>
		</Dialog>
	)
}

function Body({ props }: { props: OrgTypesEditorViewProps }): ReactElement {
	const { t } = props
	if (props.state === 'loading') return <p aria-live="polite">{t('tree.loading')}</p>
	if (props.state === 'error') {
		return <EmptyState title={t('types.error')} action={{ label: t('tree.retry'), onPress: props.onRetry }} />
	}
	if (props.rows.length === 0) return <EmptyState title={t('types.empty')} />
	return <Table columns={columns(props)} rows={props.rows} />
}

/** The tenant's unit types: ordered table, create/edit dialog with hierarchy rules, guarded delete (organism). */
export function OrgTypesEditorView(props: OrgTypesEditorViewProps): ReactElement {
	const { t } = props
	return (
		<section aria-label={t('types.title')} className="flex flex-col gap-4">
			<header className="flex items-center justify-between">
				<h2 className="text-ds-lg font-semibold text-ds-text">{t('types.title')}</h2>
				{props.onNew ? <Button onPress={props.onNew}>{t('types.new')}</Button> : null}
			</header>
			<Body props={props} />
			<EditorDialog props={props} />
			<ProductConfirmDialogView
				t={t}
				isOpen={props.deleting !== null}
				title={t('types.deleteTitle')}
				body={t('types.deleteBody', { name: props.deleting?.label ?? '' })}
				confirmLabel={t('types.confirmDelete')}
				cancelLabel={t('types.cancel')}
				danger
				error={props.deleting?.error}
				onConfirm={props.onConfirmDelete}
				onCancel={props.onCancelDelete}
			/>
		</section>
	)
}
